/**
 * Atelier « Tracé à la main » — freehand circuit editor (owner request,
 * 2026-07-18).
 *
 * The player draws a closed loop of cubic Bézier anchors (click to place,
 * click-drag to pull handles like a pen tool, click the first anchor to close),
 * then shapes it: move anchors/handles, insert (double-click) or delete
 * (right-click) points, set the corridor width per anchor with a gauge, paint
 * surfaces per portion (palette + click / drag / shift-click range), and place
 * the start line + driving direction.
 *
 * The design is rebuilt (debounced) into a real `Track` with the SAME shared
 * builder the server uses, and re-validated live (clearance / curvature /
 * width / length) with located red markers — so anything that saves here is
 * guaranteed to race exactly like it previews: checkpoints, server timing,
 * grid and records all derive from the built track.
 */

import type {
  DesignAnchor,
  SurfaceId,
  Track,
  TrackDesign,
  TrackIssue,
  TrackSample,
} from '@microligue/shared';
import {
  DESIGN_BOUNDS,
  DESIGN_VALIDATION,
  SURFACE_IDS,
  SURFACE_LABELS,
  buildTrackFromDesign,
  clampCoord,
  clampHandle,
  closestPoint,
  computeCheckpoints,
  designIssueLabelFr,
  designWarnings,
  gridSlotPose,
  insertAnchorInDesign,
  ovalTemplateDesign,
  refreshAutoHandles,
  reverseDesign,
  roundDesign,
  segmentControls,
  validateTrack,
} from '@microligue/shared';
import { SURFACE_COLORS } from './surfaces';

type EditorMode = 'draw' | 'edit' | 'width' | 'surface' | 'start';

const DRAFT_KEY = 'microligue.editor.draft.v1';
const DEFAULT_WIDTH = 160;
const REBUILD_DEBOUNCE_MS = 120;
const UNDO_LIMIT = 100;
/** Screen-px pick radii. */
const PICK_ANCHOR = 12;
const PICK_HANDLE = 10;
/** World-px extra slack around the band for segment picking. */
const PICK_BAND_SLACK = 14;
/** World-px minimum between two consecutively placed anchors (draw mode). */
const MIN_ANCHOR_SPACING = 25;
/**
 * Screen-px a press must travel before the pen gesture pulls handles out. A
 * real mouse click wobbles 1-2 px between down and up: without this threshold
 * every placed point got ~zero-length manual handles instead of smooth auto
 * ones — each click silently became a razor-sharp micro corner that tripped
 * the curvature check ("12 problèmes" on a visually clean circuit).
 */
const PEN_PULL_THRESHOLD = 8;
/**
 * Screen-px an anchor/handle grab must travel before it mutates anything and
 * banks its undo snapshot — a plain selection click (1-2 px wobble) must not
 * move geometry nor destroy the redo stack (audit G3).
 */
const DRAG_MOVE_THRESHOLD = 3;
/** Handles shorter than this (both sides) on a non-auto anchor = click wobble. */
const MICRO_HANDLE = 12;
const POLY_STEPS = 32;

interface SegPolyPoint {
  x: number;
  y: number;
  u: number;
}

interface DragState {
  kind: 'anchor' | 'handleIn' | 'handleOut' | 'pan' | 'paint' | 'pen';
  index: number;
  moved: boolean;
  lastX: number;
  lastY: number;
  /** Press position — the pen gesture engages beyond PEN_PULL_THRESHOLD. */
  pressX: number;
  pressY: number;
  /** Owning pointer — a second touch must not hijack the gesture (audit G6). */
  pointerId: number;
  /**
   * Pre-drag snapshot, pushed onto the undo stack at the FIRST movement only —
   * a plain selection click must not spend (or clear) any undo/redo history.
   */
  undoSnap?: string;
}

export interface TrackEditorCallbacks {
  onTry: (design: TrackDesign) => void;
  onSave: (design: TrackDesign, name: string) => void;
  status: (msg: string) => void;
}

export class TrackEditor {
  private design: TrackDesign = { version: 1, anchors: [], startOffset: 0 };
  private closed = false;
  private mode: EditorMode = 'draw';
  private selection: number | null = null;
  private activeSurface: SurfaceId = 'asphalt';
  private lastPaintedSeg: number | null = null;
  private lastWidth = DEFAULT_WIDTH;

  private track: Track | null = null;
  private issues: TrackIssue[] = [];
  private warnings: string[] = [];
  private checkpoints: number[] = [];

  private readonly undoStack: string[] = [];
  private readonly redoStack: string[] = [];

  private viewX = 1400;
  private viewY = 950;
  private viewScale = 0.3;
  private fitted = false;

  private drag: DragState | null = null;
  private spaceHeld = false;
  private hoverAnchor: number | null = null;
  private hoverSeg: number | null = null;
  private hoverClose = false;

  private segPolys: SegPolyPoint[][] | null = null;
  private rebuildTimer: number | null = null;
  private draftTimer: number | null = null;
  private renderQueued = false;
  private saveBusy = false;
  /** One console dump per session when band/anchors diverge (bug reporting). */
  private divergenceLogged = false;
  /** Arms one undo snapshot per width-gauge adjustment burst (audit M5). */
  private widthUndoArmed = true;

  private readonly canvas: HTMLCanvasElement;
  private readonly modeButtons = new Map<EditorMode, HTMLButtonElement>();
  private readonly els: {
    ctxWidth: HTMLElement;
    ctxSurface: HTMLElement;
    ctxStart: HTMLElement;
    hint: HTMLElement;
    widthSlider: HTMLInputElement;
    widthValue: HTMLElement;
    validity: HTMLElement;
    length: HTMLElement;
    warnings: HTMLElement;
    status: HTMLElement;
    issueJump: HTMLButtonElement;
    tryBtn: HTMLButtonElement;
    saveBtn: HTMLButtonElement;
    nameInput: HTMLInputElement;
    undoBtn: HTMLButtonElement;
    redoBtn: HTMLButtonElement;
  };
  /** Which issue the 🔍 button focuses next (cycles). */
  private issueCursor = -1;

  constructor(
    private readonly root: HTMLElement,
    private readonly cb: TrackEditorCallbacks,
  ) {
    const get = <T extends HTMLElement>(id: string): T => {
      const el = root.querySelector(`#${id}`);
      if (!el) throw new Error(`Éditeur : élément introuvable #${id}`);
      return el as T;
    };
    this.canvas = get<HTMLCanvasElement>('editor-canvas');
    this.els = {
      ctxWidth: get('ed-ctx-width'),
      ctxSurface: get('ed-ctx-surface'),
      ctxStart: get('ed-ctx-start'),
      hint: get('ed-hint'),
      widthSlider: get<HTMLInputElement>('ed-width-slider'),
      widthValue: get('ed-width-value'),
      validity: get('ed-validity'),
      length: get('ed-length'),
      warnings: get('ed-warnings'),
      status: get('ed-status'),
      issueJump: get<HTMLButtonElement>('ed-issue-jump'),
      tryBtn: get<HTMLButtonElement>('ed-try'),
      saveBtn: get<HTMLButtonElement>('ed-save'),
      nameInput: get<HTMLInputElement>('ed-name'),
      undoBtn: get<HTMLButtonElement>('ed-undo'),
      redoBtn: get<HTMLButtonElement>('ed-redo'),
    };

    this.bindToolbar(get);
    this.bindCanvas();
    this.bindKeyboard();
    this.buildSurfacePalette(get('ed-surface-palette'));

    // The backing store must track EVERY CSS size change of the canvas (tab
    // switch widening the card, window resize, scrollbar appearing…): a stale
    // size stretches the whole rendering and offsets clicks. ResizeObserver
    // catches them all, including changes no window event reports.
    //
    // It must ONLY resize — NEVER refit the view. The status row rewrapping or
    // the card scrollbar toggling shrinks the canvas by a few px mid-drag; a
    // refit here changed the world-under-cursor mapping between two pointer
    // moves and TELEPORTED the dragged anchor thousands of px away (owner:
    // « le point part complètement sur la gauche très loin »).
    const observer = new ResizeObserver(() => {
      if (this.canvas.clientWidth === 0) return;
      this.resize();
      if (!this.fitted) this.fitView();
      this.requestRender();
    });
    observer.observe(this.canvas);

    this.loadDraft();
    if (this.design.anchors.length === 0) {
      this.design = ovalTemplateDesign();
      this.closed = true;
      this.mode = 'edit';
    }
    this.setMode(this.closed ? this.mode : 'draw');
    this.mutated(false);
  }

  // -------------------------------------------------------------------------
  // Wiring
  // -------------------------------------------------------------------------

  private bindToolbar(get: <T extends HTMLElement>(id: string) => T): void {
    const modes: [EditorMode, string][] = [
      ['draw', 'ed-mode-draw'],
      ['edit', 'ed-mode-edit'],
      ['width', 'ed-mode-width'],
      ['surface', 'ed-mode-surface'],
      ['start', 'ed-mode-start'],
    ];
    for (const [mode, id] of modes) {
      const btn = get<HTMLButtonElement>(id);
      this.modeButtons.set(mode, btn);
      btn.addEventListener('click', () => this.setMode(mode));
    }

    this.els.undoBtn.addEventListener('click', () => this.undo());
    this.els.redoBtn.addEventListener('click', () => this.redo());
    this.els.issueJump.addEventListener('click', () => this.jumpToNextIssue());
    get<HTMLButtonElement>('ed-repair').addEventListener('click', () => this.repairDesign());
    get<HTMLButtonElement>('ed-copy').addEventListener('click', () => this.copyDesign());
    get<HTMLButtonElement>('ed-paste').addEventListener('click', () => this.pasteDesign());
    get<HTMLButtonElement>('ed-fit').addEventListener('click', () => {
      this.fitView();
      this.requestRender();
    });
    get<HTMLButtonElement>('ed-new-oval').addEventListener('click', () => {
      this.pushUndo();
      this.design = ovalTemplateDesign();
      this.closed = true;
      this.selection = null;
      this.setMode('edit');
      // Recenter NOW on the fresh oval — inheriting the previous circuit's
      // view (possibly far away / zoomed out) disorients every tool.
      this.track = null;
      this.fitView();
      this.mutated();
      this.cb.status('Nouvel ovale — remodelez-le à votre goût.');
    });
    get<HTMLButtonElement>('ed-new-blank').addEventListener('click', () => {
      this.pushUndo();
      this.design = { version: 1, anchors: [], startOffset: 0 };
      this.closed = false;
      this.selection = null;
      this.setMode('draw');
      // A blank sheet MUST reset the view: keeping the previous circuit's
      // frame (owner case: far-left, zoomed-out after a corrupted draft) made
      // every click land at the world position that frame pointed at — « les
      // points se mettent tous complètement à gauche de la zone ».
      this.defaultView();
      this.mutated();
      this.cb.status('Feuille blanche — cliquez pour poser le premier point.');
    });
    get<HTMLButtonElement>('ed-reverse').addEventListener('click', () => {
      if (!this.closed || !this.track) return;
      this.pushUndo();
      this.design = reverseDesign(this.design, this.track.length);
      this.mutated();
      this.cb.status('Sens de course inversé.');
    });
    get<HTMLButtonElement>('ed-width-all').addEventListener('click', () => {
      if (this.design.anchors.length === 0) return;
      this.pushUndo();
      const w = Number(this.els.widthSlider.value);
      for (const a of this.design.anchors) a.width = w;
      this.lastWidth = w;
      this.mutated();
    });

    // One undo entry per adjustment burst, banked at the FIRST effective
    // change — covers pointer drags AND keyboard arrows on the focused slider
    // (which never fire pointerdown, audit M5). 'change' re-arms.
    this.els.widthSlider.addEventListener('input', () => {
      const w = Number(this.els.widthSlider.value);
      this.els.widthValue.textContent = `${w} px`;
      if (this.selection !== null) {
        const a = this.design.anchors[this.selection];
        if (a) {
          if (this.widthUndoArmed) {
            this.widthUndoArmed = false;
            this.pushUndo();
          }
          a.width = w;
          this.lastWidth = w;
          this.mutated();
        }
      }
    });
    this.els.widthSlider.addEventListener('change', () => {
      this.widthUndoArmed = true;
    });

    this.els.tryBtn.addEventListener('click', () => {
      if (this.canShip()) this.cb.onTry(roundDesign(this.design));
    });
    this.els.saveBtn.addEventListener('click', () => {
      const name = this.els.nameInput.value.trim();
      if (!name) {
        this.cb.status('Donnez un nom au circuit avant de l’enregistrer.');
        this.els.nameInput.focus();
        return;
      }
      if (this.canShip()) {
        this.setSaveBusy(true);
        this.cb.onSave(roundDesign(this.design), name);
      }
    });
  }

  private buildSurfacePalette(box: HTMLElement): void {
    const order: SurfaceId[] = ['asphalt', 'wet', 'dirt', 'snow', 'ice'];
    for (const id of order) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ed-swatch';
      btn.style.setProperty('--swatch', SURFACE_COLORS[id]);
      btn.title = SURFACE_LABELS[id];
      btn.textContent = SURFACE_LABELS[id];
      btn.classList.toggle('active', id === this.activeSurface);
      btn.addEventListener('click', () => {
        this.activeSurface = id;
        for (const child of box.children) child.classList.remove('active');
        btn.classList.add('active');
      });
      box.appendChild(btn);
    }
  }

  private bindCanvas(): void {
    this.canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
    this.canvas.addEventListener('pointercancel', () => {
      this.drag = null;
    });
    this.canvas.addEventListener('dblclick', (e) => this.onDoubleClick(e));
    this.canvas.addEventListener('contextmenu', (e) => this.onContextMenu(e));
    this.canvas.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
  }

  private bindKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      if (!this.isActive() || isEditingContext(e)) return;
      if (e.code === 'Space') {
        // Space on a focused toolbar button must ACTIVATE it (audit M8).
        if (e.target instanceof HTMLButtonElement) return;
        this.spaceHeld = true;
        e.preventDefault();
        return;
      }
      // No structural edits while a pointer drag is running: undo/delete/pop
      // would splice the anchors array under the drag's stored index and the
      // move handler would then mutate the WRONG anchor.
      if (this.drag) return;
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ' && !e.shiftKey) {
        e.preventDefault();
        this.undo();
      } else if (
        (e.ctrlKey || e.metaKey) &&
        (e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey))
      ) {
        e.preventDefault();
        this.redo();
      } else if (e.code === 'Escape') {
        if (this.mode === 'draw' && !this.closed && this.design.anchors.length > 0) {
          this.pushUndo();
          this.design.anchors.pop();
          this.selection = null; // could point past the shrunk array (audit M6)
          this.mutated();
        } else {
          this.selection = null;
          this.requestRender();
        }
      } else if (e.code === 'Delete' || e.code === 'Backspace') {
        if (this.selection !== null) {
          e.preventDefault();
          this.deleteAnchor(this.selection);
        }
      }
    });
    window.addEventListener('keyup', (e) => {
      if (e.code === 'Space') this.spaceHeld = false;
    });
    window.addEventListener('blur', () => {
      this.spaceHeld = false;
    });
    // Closing the tab right after an edit must not lose the debounced draft
    // save (audit M9).
    window.addEventListener('beforeunload', () => {
      if (this.draftTimer !== null) {
        window.clearTimeout(this.draftTimer);
        this.saveDraft();
      }
    });
    window.addEventListener('resize', () => {
      if (this.isActive()) this.resize();
    });
  }

  /** The editor reacts to keys only while its tab is on screen. */
  private isActive(): boolean {
    return !this.root.hidden && this.root.offsetParent !== null;
  }

  /** Called when the « Tracé à la main » tab becomes visible. */
  open(): void {
    this.resize();
    if (!this.fitted) this.fitView();
    // Sanity: an empty sheet framed on a far-away / microscopic view (leftover
    // of a previous circuit) makes drawing land nowhere near the clicks.
    if (
      this.design.anchors.length === 0 &&
      (this.viewScale < 0.15 || Math.abs(this.viewX) > 10000 || Math.abs(this.viewY) > 10000)
    ) {
      this.defaultView();
    }
    this.updateHint();
    this.requestRender();
  }

  /** Comfortable authoring frame: origin area at a scale fit for drawing. */
  private defaultView(): void {
    this.viewX = 1400;
    this.viewY = 950;
    this.viewScale = 0.38;
    this.fitted = true;
  }

  setSaveBusy(busy: boolean): void {
    this.saveBusy = busy;
    this.refreshShipButtons();
  }

  /** Feedback line INSIDE the editor (the shared card status sits far below). */
  showMessage(msg: string): void {
    this.els.status.textContent = msg;
  }

  notifySaved(): void {
    this.setSaveBusy(false);
    this.els.nameInput.value = '';
    this.showMessage(
      '✓ Enregistré — retrouvez-le dans « Circuits enregistrés » en bas, et dans Jouer (marqué brouillon tant qu’un admin ne l’a pas validé).',
    );
  }

  // -------------------------------------------------------------------------
  // Geometry helpers
  // -------------------------------------------------------------------------

  private cssSize(): { w: number; h: number } {
    return { w: this.canvas.clientWidth, h: this.canvas.clientHeight };
  }

  private toWorld(e: PointerEvent | MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    return {
      x: (px - rect.width / 2) / this.viewScale + this.viewX,
      y: (py - rect.height / 2) / this.viewScale + this.viewY,
    };
  }

  private sx(wx: number): number {
    return (wx - this.viewX) * this.viewScale + this.cssSize().w / 2;
  }

  private sy(wy: number): number {
    return (wy - this.viewY) * this.viewScale + this.cssSize().h / 2;
  }

  private polylines(): SegPolyPoint[][] {
    if (this.segPolys) return this.segPolys;
    const n = this.design.anchors.length;
    const count = this.closed ? n : Math.max(0, n - 1);
    const polys: SegPolyPoint[][] = [];
    for (let i = 0; i < count; i++) {
      const c = segmentControls(this.design, i);
      const pts: SegPolyPoint[] = [];
      for (let k = 0; k <= POLY_STEPS; k++) {
        const u = k / POLY_STEPS;
        const v = 1 - u;
        pts.push({
          x: v * v * v * c.x0 + 3 * v * v * u * c.x1 + 3 * v * u * u * c.x2 + u * u * u * c.x3,
          y: v * v * v * c.y0 + 3 * v * v * u * c.y1 + 3 * v * u * u * c.y2 + u * u * u * c.y3,
          u,
        });
      }
      polys.push(pts);
    }
    this.segPolys = polys;
    return polys;
  }

  private pickAnchor(world: { x: number; y: number }): number | null {
    const rPick = PICK_ANCHOR / this.viewScale;
    let best: number | null = null;
    let bestD = rPick;
    this.design.anchors.forEach((a, i) => {
      const d = Math.hypot(a.x - world.x, a.y - world.y);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  }

  private pickHandle(world: { x: number; y: number }): 'in' | 'out' | null {
    if (this.selection === null) return null;
    const a = this.design.anchors[this.selection];
    if (!a) return null;
    const rPick = PICK_HANDLE / this.viewScale;
    // A handle whose knob sits on top of its anchor (zoomed way out) must not
    // shadow the anchor grab — grabbing it would silently de-smooth the point
    // (audit G4). 14 screen px of handle length minimum to be pickable.
    const minLen = 14 / this.viewScale;
    const dIn = Math.hypot(a.x + a.inX - world.x, a.y + a.inY - world.y);
    const dOut = Math.hypot(a.x + a.outX - world.x, a.y + a.outY - world.y);
    const outOk = Math.hypot(a.outX, a.outY) >= minLen;
    const inOk = Math.hypot(a.inX, a.inY) >= minLen;
    if (outOk && dOut < rPick && (dOut <= dIn || !inOk)) return 'out';
    if (inOk && dIn < rPick) return 'in';
    return null;
  }

  /** Nearest design segment within the band (plus slack). */
  private pickSegment(world: { x: number; y: number }): { seg: number; u: number } | null {
    const polys = this.polylines();
    let best: { seg: number; u: number; d: number } | null = null;
    for (let i = 0; i < polys.length; i++) {
      const pts = polys[i] as SegPolyPoint[];
      const a = this.design.anchors[i] as DesignAnchor;
      const b = this.design.anchors[(i + 1) % this.design.anchors.length] as DesignAnchor;
      const maxHalf = Math.max(a.width, b.width) / 2 + PICK_BAND_SLACK;
      for (const p of pts) {
        const d = Math.hypot(p.x - world.x, p.y - world.y);
        if (d <= maxHalf && (best === null || d < best.d)) {
          best = { seg: i, u: p.u, d };
        }
      }
    }
    return best ? { seg: best.seg, u: best.u } : null;
  }

  // -------------------------------------------------------------------------
  // Interactions
  // -------------------------------------------------------------------------

  private onPointerDown(e: PointerEvent): void {
    // Left (0) draws/edits, middle (1) pans; right goes to contextmenu; the
    // browser-nav side buttons (3/4) must not place anchors (audit M1).
    if (e.button !== 0 && e.button !== 1) return;
    // One gesture at a time: a second touch must neither hijack the running
    // drag nor drop a stray anchor mid-pinch (audit G6).
    if (this.drag) return;
    this.canvas.setPointerCapture(e.pointerId);
    const world = this.toWorld(e);
    const pan = e.button === 1 || this.spaceHeld;

    if (pan) {
      this.drag = {
        kind: 'pan',
        index: -1,
        moved: false,
        lastX: e.clientX,
        lastY: e.clientY,
        pressX: e.clientX,
        pressY: e.clientY,
        pointerId: e.pointerId,
      };
      return;
    }

    if (this.mode === 'draw' && !this.closed) {
      // Close the loop by clicking the first anchor (3+ points placed).
      const hit = this.pickAnchor(world);
      if (hit === 0 && this.design.anchors.length >= 3) {
        this.pushUndo();
        this.closed = true;
        refreshAutoHandles(this.design.anchors);
        this.setMode('edit');
        this.mutated();
        this.cb.status('Boucle fermée ✓ — ajustez le tracé, les largeurs et les surfaces.');
        return;
      }
      // Clicking an existing point must not stack a duplicate on top of it.
      if (hit !== null) {
        this.cb.status('Point déjà posé ici — le premier point (vert) ferme la boucle.');
        return;
      }
      const last = this.design.anchors[this.design.anchors.length - 1];
      if (last && Math.hypot(last.x - world.x, last.y - world.y) < MIN_ANCHOR_SPACING) {
        // Double-click or twitch: no degenerate micro-segment — say so instead
        // of swallowing the click silently (audit M2).
        this.cb.status('Trop près du point précédent — espacez un peu vos points.');
        return;
      }
      if (this.design.anchors.length >= DESIGN_BOUNDS.anchors.max) {
        this.cb.status(`Maximum ${DESIGN_BOUNDS.anchors.max} points.`);
        return;
      }
      this.pushUndo();
      const anchor: DesignAnchor = {
        x: clampCoord(world.x),
        y: clampCoord(world.y),
        inX: 0,
        inY: 0,
        outX: 0,
        outY: 0,
        width: this.lastWidth,
        surface: this.activeSurface,
        auto: true,
      };
      this.design.anchors.push(anchor);
      this.selection = this.design.anchors.length - 1;
      // Pen gesture: dragging before release pulls the handles out.
      this.drag = {
        kind: 'pen',
        index: this.selection,
        moved: false,
        lastX: e.clientX,
        lastY: e.clientY,
        pressX: e.clientX,
        pressY: e.clientY,
        pointerId: e.pointerId,
      };
      this.mutated();
      return;
    }

    if (this.mode === 'edit') {
      const handle = this.pickHandle(world);
      if (handle && this.selection !== null) {
        this.drag = {
          kind: handle === 'in' ? 'handleIn' : 'handleOut',
          index: this.selection,
          moved: false,
          lastX: e.clientX,
          lastY: e.clientY,
          pressX: e.clientX,
          pressY: e.clientY,
          pointerId: e.pointerId,
          undoSnap: this.snapshotState(),
        };
        return;
      }
      const hit = this.pickAnchor(world);
      if (hit !== null) {
        this.selection = hit;
        this.drag = {
          kind: 'anchor',
          index: hit,
          moved: false,
          lastX: e.clientX,
          lastY: e.clientY,
          pressX: e.clientX,
          pressY: e.clientY,
          pointerId: e.pointerId,
          undoSnap: this.snapshotState(),
        };
        this.requestRender();
        return;
      }
      this.selection = null;
      this.drag = {
        kind: 'pan',
        index: -1,
        moved: false,
        lastX: e.clientX,
        lastY: e.clientY,
        pressX: e.clientX,
        pressY: e.clientY,
        pointerId: e.pointerId,
      };
      this.requestRender();
      return;
    }

    if (this.mode === 'width') {
      const hit = this.pickAnchor(world);
      if (hit !== null) {
        this.selection = hit;
        this.syncWidthControls();
        this.requestRender();
        return;
      }
      this.drag = {
        kind: 'pan',
        index: -1,
        moved: false,
        lastX: e.clientX,
        lastY: e.clientY,
        pressX: e.clientX,
        pressY: e.clientY,
        pointerId: e.pointerId,
      };
      return;
    }

    if (this.mode === 'surface') {
      const hit = this.pickSegment(world);
      if (hit !== null) {
        this.pushUndo();
        if (e.shiftKey && this.lastPaintedSeg !== null) {
          this.paintRange(this.lastPaintedSeg, hit.seg);
        } else {
          this.paintSegment(hit.seg);
        }
        this.lastPaintedSeg = hit.seg;
        this.drag = {
          kind: 'paint',
          index: hit.seg,
          moved: false,
          lastX: e.clientX,
          lastY: e.clientY,
          pressX: e.clientX,
          pressY: e.clientY,
          pointerId: e.pointerId,
        };
        this.mutated();
        return;
      }
      this.drag = {
        kind: 'pan',
        index: -1,
        moved: false,
        lastX: e.clientX,
        lastY: e.clientY,
        pressX: e.clientX,
        pressY: e.clientY,
        pointerId: e.pointerId,
      };
      return;
    }

    if (this.mode === 'start') {
      if (this.track) {
        const q = closestPoint(this.track, world);
        if (q.dist <= q.halfWidth + PICK_BAND_SLACK * 2) {
          this.pushUndo();
          let offset = (this.design.startOffset + q.s) % this.track.length;
          if (offset < 0) offset += this.track.length;
          this.design.startOffset = offset;
          this.mutated();
          this.cb.status('Ligne de départ déplacée.');
          return;
        }
      }
      this.drag = {
        kind: 'pan',
        index: -1,
        moved: false,
        lastX: e.clientX,
        lastY: e.clientY,
        pressX: e.clientX,
        pressY: e.clientY,
        pointerId: e.pointerId,
      };
    }
  }

  private onPointerMove(e: PointerEvent): void {
    const world = this.toWorld(e);

    if (this.drag) {
      const drag = this.drag;
      if (e.pointerId !== drag.pointerId) return; // second finger: not yours
      const dxScreen = e.clientX - drag.lastX;
      const dyScreen = e.clientY - drag.lastY;
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      // Anchor/handle grabs need a real intent before they mutate anything: a
      // 1-2 px click wobble must neither move geometry nor spend undo/redo
      // history (audit G3). Once the threshold is crossed, the withheld
      // distance is applied in one catch-up step so the grab doesn't lag.
      let applyDx = dxScreen;
      let applyDy = dyScreen;
      if (!drag.moved) {
        const gated =
          drag.kind === 'anchor' || drag.kind === 'handleIn' || drag.kind === 'handleOut';
        const fromPress = Math.hypot(e.clientX - drag.pressX, e.clientY - drag.pressY);
        if (!gated || fromPress >= DRAG_MOVE_THRESHOLD) {
          drag.moved = true;
          // The gesture becomes a real mutation now: bank the pre-drag snapshot.
          if (drag.undoSnap !== undefined) this.pushUndoRaw(drag.undoSnap);
          applyDx = e.clientX - drag.pressX;
          applyDy = e.clientY - drag.pressY;
        }
      }

      switch (drag.kind) {
        case 'pan':
          this.viewX -= dxScreen / this.viewScale;
          this.viewY -= dyScreen / this.viewScale;
          this.requestRender();
          return;
        case 'anchor': {
          const a = this.design.anchors[drag.index];
          if (a && drag.moved) {
            // Incremental (cursor delta ÷ scale), NOT absolute toWorld: even if
            // the view shifted between two moves, the anchor follows the hand
            // instead of teleporting to the remapped cursor position.
            a.x = clampCoord(a.x + applyDx / this.viewScale);
            a.y = clampCoord(a.y + applyDy / this.viewScale);
            this.mutated();
          }
          return;
        }
        case 'pen': {
          const a = this.design.anchors[drag.index];
          if (!a) return;
          // Engage only past a real pull: click wobble must NOT overwrite the
          // smooth auto handles with a ~zero-length manual pair.
          const pulled = Math.hypot(e.clientX - drag.pressX, e.clientY - drag.pressY);
          if (pulled >= PEN_PULL_THRESHOLD) {
            // Handle = total pull since the press, in screen px ÷ zoom: no
            // rect/world lookup at all, so a mid-drag canvas resize cannot
            // remap it (audit B1).
            a.outX = clampHandle((e.clientX - drag.pressX) / this.viewScale);
            a.outY = clampHandle((e.clientY - drag.pressY) / this.viewScale);
            a.inX = -a.outX;
            a.inY = -a.outY;
            a.auto = false;
            this.mutated();
          }
          return;
        }
        case 'handleIn':
        case 'handleOut': {
          const a = this.design.anchors[drag.index];
          if (!a || !drag.moved) return;
          // Incremental like the anchors (audit B1): a mid-drag canvas resize
          // must not remap the handle to the shifted cursor position.
          const out = drag.kind === 'handleOut';
          const hx = clampHandle((out ? a.outX : a.inX) + applyDx / this.viewScale);
          const hy = clampHandle((out ? a.outY : a.inY) + applyDy / this.viewScale);
          a.auto = false;
          if (out) {
            a.outX = hx;
            a.outY = hy;
            if (!e.altKey) {
              // Keep the opposite handle collinear, preserving its length.
              const len = Math.hypot(a.inX, a.inY);
              const norm = Math.hypot(hx, hy);
              if (norm > 1e-6) {
                a.inX = (-hx / norm) * len;
                a.inY = (-hy / norm) * len;
              }
            }
          } else {
            a.inX = hx;
            a.inY = hy;
            if (!e.altKey) {
              const len = Math.hypot(a.outX, a.outY);
              const norm = Math.hypot(hx, hy);
              if (norm > 1e-6) {
                a.outX = (-hx / norm) * len;
                a.outY = (-hy / norm) * len;
              }
            }
          }
          this.mutated();
          return;
        }
        case 'paint': {
          const hit = this.pickSegment(world);
          if (hit !== null && hit.seg !== this.lastPaintedSeg) {
            this.paintSegment(hit.seg);
            this.lastPaintedSeg = hit.seg;
            this.mutated();
          }
          return;
        }
      }
    }

    // Hover feedback.
    const prevAnchor = this.hoverAnchor;
    const prevSeg = this.hoverSeg;
    const prevClose = this.hoverClose;
    this.hoverAnchor = this.pickAnchor(world);
    this.hoverSeg =
      this.mode === 'surface' || this.mode === 'edit'
        ? (this.pickSegment(world)?.seg ?? null)
        : null;
    this.hoverClose =
      this.mode === 'draw' &&
      !this.closed &&
      this.design.anchors.length >= 3 &&
      this.hoverAnchor === 0;
    if (
      prevAnchor !== this.hoverAnchor ||
      prevSeg !== this.hoverSeg ||
      prevClose !== this.hoverClose
    ) {
      this.requestRender();
    }
    this.canvas.style.cursor =
      this.hoverAnchor !== null || this.hoverSeg !== null
        ? 'pointer'
        : this.spaceHeld
          ? 'grab'
          : 'crosshair';
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.canvas.hasPointerCapture(e.pointerId)) {
      this.canvas.releasePointerCapture(e.pointerId);
    }
    const drag = this.drag;
    if (drag && e.pointerId !== drag.pointerId) return; // another finger lifted
    // A pen pull that ends microscopic IN WORLD PX (possible at high zoom:
    // 8 screen px ÷ zoom 3 ≈ 2.7 px) would leave a razor micro-corner — the
    // very defect the pen threshold exists to prevent. Re-smooth it.
    if (drag && drag.kind === 'pen') {
      const a = this.design.anchors[drag.index];
      if (a && !a.auto && Math.hypot(a.outX, a.outY) < MICRO_HANDLE) {
        a.auto = true;
        this.mutated();
      }
    }
    // A non-moved anchor/handle press was a plain selection click: its undo
    // snapshot was never banked (see onPointerMove), nothing to clean up.
    this.drag = null;
  }

  private onDoubleClick(e: MouseEvent): void {
    if (this.mode !== 'edit' || !this.closed) return;
    const world = this.toWorld(e);
    if (this.pickAnchor(world) !== null) return;
    const hit = this.pickSegment(world);
    if (!hit) return;
    if (this.design.anchors.length >= DESIGN_BOUNDS.anchors.max) {
      this.cb.status(`Maximum ${DESIGN_BOUNDS.anchors.max} points.`);
      return;
    }
    this.pushUndo();
    this.design = insertAnchorInDesign(this.design, hit.seg, hit.u);
    this.selection = hit.seg + 1;
    this.lastPaintedSeg = null;
    this.mutated();
    this.cb.status('Point inséré sur la courbe.');
  }

  private onContextMenu(e: MouseEvent): void {
    e.preventDefault();
    if (this.drag) return; // no splicing under a live drag's stored index
    if (this.mode !== 'edit' && this.mode !== 'draw') return;
    const world = this.toWorld(e);
    const hit = this.pickAnchor(world);
    if (hit !== null) this.deleteAnchor(hit);
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const before = {
      x: (px - rect.width / 2) / this.viewScale + this.viewX,
      y: (py - rect.height / 2) / this.viewScale + this.viewY,
    };
    const factor = Math.exp(-e.deltaY * 0.0012);
    this.viewScale = Math.min(Math.max(this.viewScale * factor, 0.04), 3);
    // Keep the world point under the cursor fixed.
    this.viewX = before.x - (px - rect.width / 2) / this.viewScale;
    this.viewY = before.y - (py - rect.height / 2) / this.viewScale;
    this.requestRender();
  }

  private deleteAnchor(index: number): void {
    const min = this.closed ? DESIGN_BOUNDS.anchors.min : 1;
    if (this.design.anchors.length <= min && this.closed) {
      this.cb.status(`Un circuit garde au moins ${DESIGN_BOUNDS.anchors.min} points.`);
      return;
    }
    if (this.design.anchors.length === 0) return;
    this.drag = null; // never splice under a live drag's stored index (audit B2)
    this.pushUndo();
    this.design.anchors.splice(index, 1);
    this.lastPaintedSeg = null;
    if (this.selection !== null) {
      if (this.selection === index) this.selection = null;
      else if (this.selection > index) this.selection--;
    }
    if (this.design.anchors.length < 3 && this.closed) {
      this.closed = false;
      this.setMode('draw');
    }
    this.mutated();
  }

  private paintSegment(seg: number): void {
    const a = this.design.anchors[seg];
    if (a) a.surface = this.activeSurface;
  }

  /** Paints the arc from segment `from` to segment `to`, following the lap. */
  private paintRange(from: number, to: number): void {
    const n = this.design.anchors.length;
    let i = ((from % n) + n) % n;
    const end = ((to % n) + n) % n;
    for (let guard = 0; guard <= n; guard++) {
      this.paintSegment(i);
      if (i === end) break;
      i = (i + 1) % n;
    }
  }

  // -------------------------------------------------------------------------
  // Mode / state plumbing
  // -------------------------------------------------------------------------

  private setMode(mode: EditorMode): void {
    this.drag = null; // a mode switch invalidates any gesture in flight
    this.mode = mode;
    for (const [m, btn] of this.modeButtons) btn.classList.toggle('active', m === mode);
    this.els.ctxWidth.hidden = mode !== 'width';
    this.els.ctxSurface.hidden = mode !== 'surface';
    this.els.ctxStart.hidden = mode !== 'start';
    if (mode === 'width') this.syncWidthControls();
    this.lastPaintedSeg = null;
    this.updateHint();
    this.requestRender();
  }

  private updateHint(): void {
    const hints: Record<EditorMode, string> = {
      draw: this.closed
        ? 'Boucle déjà fermée — « Nouveau : vide » pour repartir de zéro.'
        : 'Clic : poser un point (glisser = courber) · clic sur le 1er point : fermer · Échap : retirer · molette : zoom · clic milieu/Espace : déplacer la vue.',
      edit: 'Glissez points et poignées (Alt : casser la symétrie) · double-clic sur la piste : insérer · clic droit : supprimer · Ctrl+Z : annuler.',
      width:
        'Cliquez un point puis réglez sa largeur à la jauge — la piste peut être large ici, étroite là.',
      surface:
        'Choisissez un revêtement puis cliquez les portions (glisser = peindre en continu, Maj+clic = toute la section depuis le dernier clic).',
      start:
        'Cliquez sur la piste pour poser la ligne de départ — la grille et les points de passage s’affichent.',
    };
    this.els.hint.textContent = hints[this.mode];
  }

  private syncWidthControls(): void {
    const a = this.selection !== null ? this.design.anchors[this.selection] : undefined;
    const w = a ? a.width : this.lastWidth;
    this.els.widthSlider.value = String(Math.round(w));
    this.els.widthValue.textContent = a ? `${Math.round(w)} px` : '— (sélectionnez un point)';
    this.widthUndoArmed = true;
  }

  private snapshotState(): string {
    return JSON.stringify({ design: this.design, closed: this.closed });
  }

  private pushUndoRaw(snap: string): void {
    this.undoStack.push(snap);
    if (this.undoStack.length > UNDO_LIMIT) this.undoStack.shift();
    this.redoStack.length = 0;
    this.refreshUndoButtons();
  }

  private pushUndo(): void {
    this.pushUndoRaw(this.snapshotState());
  }

  private undo(): void {
    const snap = this.undoStack.pop();
    if (!snap) return;
    this.redoStack.push(this.snapshotState());
    this.restore(snap);
  }

  private redo(): void {
    const snap = this.redoStack.pop();
    if (!snap) return;
    this.undoStack.push(this.snapshotState());
    this.restore(snap);
  }

  private restore(snap: string): void {
    try {
      const parsed = JSON.parse(snap) as { design: TrackDesign; closed: boolean };
      this.design = parsed.design;
      this.closed = parsed.closed;
    } catch {
      return;
    }
    this.drag = null; // the design was swapped under any running gesture (B2)
    this.selection = null;
    this.lastPaintedSeg = null;
    if (!this.closed && this.mode !== 'draw') this.setMode('draw');
    // Redoing a loop closure must land in edit mode, not a dead draw+closed
    // state where no pointer branch applies (audit G1).
    if (this.closed && this.mode === 'draw') this.setMode('edit');
    this.mutated();
  }

  private refreshUndoButtons(): void {
    this.els.undoBtn.disabled = this.undoStack.length === 0;
    this.els.redoBtn.disabled = this.redoStack.length === 0;
  }

  /** Every design mutation funnels here: refresh caches, rebuild, persist. */
  private mutated(persist = true): void {
    refreshAutoHandles(this.design.anchors);
    this.segPolys = null;
    this.refreshUndoButtons();
    if (this.mode === 'width') this.syncWidthControls();
    if (this.rebuildTimer !== null) window.clearTimeout(this.rebuildTimer);
    this.rebuildTimer = window.setTimeout(() => this.rebuild(), REBUILD_DEBOUNCE_MS);
    if (persist) {
      if (this.draftTimer !== null) window.clearTimeout(this.draftTimer);
      this.draftTimer = window.setTimeout(() => this.saveDraft(), 400);
    }
    this.requestRender();
  }

  private rebuild(): void {
    this.rebuildTimer = null;
    this.track = null;
    this.issues = [];
    this.warnings = [];
    this.checkpoints = [];
    this.issueCursor = -1;
    if (this.closed && this.design.anchors.length >= DESIGN_BOUNDS.anchors.min) {
      try {
        // Build and judge EXACTLY what would be sent (rounded to 0.1 px): a
        // shape sitting on the geometric limit must get the same verdict here
        // and on the server — the owner hit « ✅ valide » locally followed by a
        // server refusal because the unrounded design was validated instead.
        this.track = buildTrackFromDesign(roundDesign(this.design));
        const result = validateTrack(this.track, DESIGN_VALIDATION);
        this.issues = result.issues;
        this.warnings = result.valid ? designWarnings(this.track) : [];
        this.checkpoints = computeCheckpoints(this.track);
      } catch (err) {
        console.error('éditeur : échec de construction du circuit', err);
        this.track = null;
        this.issues = [{ code: 'samples', message: 'tracé dégénéré' }];
      }
    }
    this.refreshStatus();
    this.requestRender();
  }

  private canShip(): boolean {
    return (
      this.closed &&
      this.track !== null &&
      this.issues.length === 0 &&
      this.design.anchors.length >= DESIGN_BOUNDS.anchors.min
    );
  }

  private refreshShipButtons(): void {
    const ok = this.canShip();
    this.els.tryBtn.disabled = !ok;
    this.els.saveBtn.disabled = !ok || this.saveBusy;
  }

  private refreshStatus(): void {
    const v = this.els.validity;
    if (!this.closed) {
      v.textContent = `✏️ Tracé en cours (${this.design.anchors.length} point${this.design.anchors.length > 1 ? 's' : ''})`;
      v.className = 'ed-chip';
      this.els.length.textContent =
        this.design.anchors.length >= 3 ? 'Cliquez le premier point pour fermer la boucle.' : '';
    } else if (this.track && this.issues.length === 0) {
      v.textContent = '✅ Circuit valide';
      v.className = 'ed-chip ok';
      this.els.length.textContent = `Longueur : ${Math.round(this.track.length)} px · ${this.checkpoints.length} points de passage`;
    } else {
      const first = this.issues[0];
      v.textContent = `⚠ ${this.issues.length} problème${this.issues.length > 1 ? 's' : ''} — ${first ? designIssueLabelFr(first) : ''}`;
      v.className = 'ed-chip bad';
      this.els.length.textContent = this.track
        ? `Longueur : ${Math.round(this.track.length)} px`
        : '';
    }
    this.els.warnings.textContent = this.warnings.join(' · ');
    const jumpable = this.issues.filter((i) => i.s !== undefined).length;
    this.els.issueJump.hidden = !this.closed || jumpable === 0 || this.track === null;
    if (!this.els.issueJump.hidden) {
      this.els.issueJump.textContent =
        jumpable > 1 ? `🔍 Voir les problèmes (${jumpable})` : '🔍 Voir le problème';
    }
    this.refreshShipButtons();
  }

  /** Cycles the view through the located problems, one 🔍 click at a time. */
  private jumpToNextIssue(): void {
    if (!this.track) return;
    const located = this.issues.filter((i) => i.s !== undefined);
    if (located.length === 0) return;
    this.issueCursor = (this.issueCursor + 1) % located.length;
    const issue = located[this.issueCursor];
    if (!issue || issue.s === undefined) return;
    const idx = Math.round(issue.s / this.track.step) % this.track.samples.length;
    const smp = this.track.samples[idx] as TrackSample;
    this.viewX = smp.x;
    this.viewY = smp.y;
    this.viewScale = Math.min(Math.max(this.viewScale, 0.55), 1.2);
    this.els.issueJump.textContent = `🔍 Problème ${this.issueCursor + 1}/${located.length}`;
    this.cb.status(designIssueLabelFr(issue));
    this.requestRender();
  }

  // -------------------------------------------------------------------------
  // Export / import (sharing, backups — and remote troubleshooting)
  // -------------------------------------------------------------------------

  /** Serializes the current design as portable text (📋). */
  private copyDesign(): void {
    const payload = JSON.stringify({ design: roundDesign(this.design), closed: this.closed });
    console.info('éditeur : tracé exporté :', payload);
    try {
      // execCommand path: works on plain-HTTP origins where the async
      // Clipboard API is unavailable (the dev site has no TLS).
      const ta = document.createElement('textarea');
      ta.value = payload;
      ta.style.position = 'fixed';
      ta.style.left = '-10000px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      this.showMessage(
        ok
          ? '📋 Tracé copié — collez-le où vous voulez (sauvegarde, partage, dépannage).'
          : 'Copie refusée — le tracé est affiché dans la console (F12).',
      );
    } catch {
      this.showMessage('Copie impossible ici — le tracé est affiché dans la console (F12).');
    }
  }

  /** Loads a design exported with 📋 (undo-able). */
  private pasteDesign(): void {
    const raw = window.prompt('Collez ici un tracé exporté avec 📋 :');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { design?: TrackDesign; closed?: boolean };
      const d = parsed.design ?? (parsed as unknown as TrackDesign);
      if (!d || !Array.isArray(d.anchors) || d.anchors.length < 1) throw new Error('format');
      if (d.anchors.length > DESIGN_BOUNDS.anchors.max) throw new Error('trop de points');
      for (const a of d.anchors) {
        if (![a.x, a.y, a.inX, a.inY, a.outX, a.outY, a.width].every(Number.isFinite)) {
          throw new Error('nombre invalide');
        }
        if (!SURFACE_IDS.includes(a.surface)) a.surface = 'asphalt';
      }
      this.pushUndo();
      this.design = roundDesign({
        version: 1,
        anchors: d.anchors,
        startOffset: Number.isFinite(d.startOffset) ? d.startOffset : 0,
      });
      this.closed = (parsed.closed ?? true) === true && this.design.anchors.length >= 3;
      this.selection = null;
      this.healMicroHandles();
      this.setMode(this.closed ? 'edit' : 'draw');
      this.track = null;
      this.fitView();
      this.mutated();
      this.showMessage('📥 Tracé importé ✓');
    } catch {
      this.showMessage('Tracé illisible — collez exactement le texte exporté par 📋.');
    }
  }

  // -------------------------------------------------------------------------
  // Draft persistence
  // -------------------------------------------------------------------------

  private saveDraft(): void {
    this.draftTimer = null;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ design: this.design, closed: this.closed }));
    } catch {
      // storage full/blocked — the draft is a convenience, not a requirement
    }
  }

  private loadDraft(): void {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { design?: TrackDesign; closed?: boolean };
      if (
        parsed.design &&
        Array.isArray(parsed.design.anchors) &&
        parsed.design.anchors.length <= DESIGN_BOUNDS.anchors.max
      ) {
        // Same structural validation as 📥 (audit M3): a corrupted draft
        // (NaN/null numbers) must not brick the editor and then get silently
        // overwritten by the next auto-save.
        for (const a of parsed.design.anchors) {
          if (![a.x, a.y, a.inX, a.inY, a.outX, a.outY, a.width].every(Number.isFinite)) {
            throw new Error('brouillon corrompu');
          }
          if (!SURFACE_IDS.includes(a.surface)) a.surface = 'asphalt';
        }
        this.design = parsed.design;
        this.closed = parsed.closed === true && this.design.anchors.length >= 3;
        this.mode = this.closed ? 'edit' : 'draw';
        this.healMicroHandles();
        this.healEjectedAnchors();
      }
    } catch {
      // Corrupted draft: keep a rescue copy, then start fresh.
      try {
        if (raw) localStorage.setItem(`${DRAFT_KEY}.secours`, raw);
      } catch {
        // storage full — nothing more we can do
      }
      console.warn('éditeur : brouillon illisible, copie de secours conservée');
    }
  }

  /**
   * Heals the artifact of the mid-drag view-refit bug: anchors teleported and
   * clamped onto the drawing-area boundary (±20000). Nobody authors a point
   * there on purpose — bring each one back to the midpoint of its neighbours
   * (the closest guess to the intended shape) with smooth auto handles.
   */
  private healEjectedAnchors(): void {
    const limit = DESIGN_BOUNDS.coord.max - 1;
    const healed = this.repairOutliers((a) => Math.abs(a.x) >= limit || Math.abs(a.y) >= limit);
    if (healed > 0) {
      console.info(`éditeur : ${healed} point(s) éjecté(s) hors zone ramené(s) près du tracé`);
      this.cb.status(
        `${healed} point(s) éjecté(s) par l'ancien bug ont été ramenés près du tracé — replacez-les si besoin.`,
      );
    }
  }

  /**
   * Repositions every anchor flagged by `isBad` back between its sane
   * neighbours. Consecutive bad anchors are SPREAD evenly along the
   * prev→next chord (stacking them all on one midpoint produced degenerate
   * duplicate points). Returns the number of repositioned anchors.
   */
  private repairOutliers(isBad: (a: DesignAnchor) => boolean): number {
    const anchors = this.design.anchors;
    const n = anchors.length;
    if (n < 3) return 0;
    const bad = anchors.map((a) => isBad(a));
    const badCount = bad.filter(Boolean).length;
    if (badCount === 0 || badCount > n - 2) return 0; // need 2 sane ends

    let healed = 0;
    for (let i = 0; i < n; i++) {
      const prevIdx = (((i - 1) % n) + n) % n;
      if (!bad[i] || bad[prevIdx]) continue; // only run starts
      // Measure the run of consecutive bad anchors beginning at i.
      let run = 0;
      while (bad[(i + run) % n]) run++;
      const prev = anchors[prevIdx] as DesignAnchor;
      const next = anchors[(i + run) % n] as DesignAnchor;
      for (let k = 0; k < run; k++) {
        const a = anchors[(i + k) % n] as DesignAnchor;
        const t = (k + 1) / (run + 1);
        a.x = prev.x + (next.x - prev.x) * t;
        a.y = prev.y + (next.y - prev.y) * t;
        a.auto = true;
        healed++;
      }
      // The junction anchors' manual handles pointed at geometry that no
      // longer exists — re-smooth them too, or they leave sharp corners.
      prev.auto = true;
      next.auto = true;
    }
    return healed;
  }

  /**
   * On-demand repair (🩹): micro-handles, boundary-ejected anchors AND
   * statistical outliers — anchors sitting far outside the cluster formed by
   * the rest of the circuit (leftovers of the teleport bug that were flung
   * without reaching the boundary clamp). Undo-able.
   */
  private repairDesign(): void {
    const anchors = this.design.anchors;
    if (anchors.length < 3) {
      this.showMessage('Rien à réparer sur un tracé ouvert de moins de 3 points.');
      return;
    }
    this.pushUndo();
    let healed = 0;
    for (const a of anchors) {
      if (a.auto) continue;
      if (Math.hypot(a.inX, a.inY) < MICRO_HANDLE && Math.hypot(a.outX, a.outY) < MICRO_HANDLE) {
        a.auto = true;
        healed++;
      }
    }
    const median = (values: number[]): number => {
      const sorted = [...values].sort((a, b) => a - b);
      return sorted[Math.floor(sorted.length / 2)] ?? 0;
    };
    const cx = median(anchors.map((a) => a.x));
    const cy = median(anchors.map((a) => a.y));
    const dMed = median(anchors.map((a) => Math.hypot(a.x - cx, a.y - cy)));
    const limit = Math.max(dMed * 4, dMed + 2500);
    const boundary = DESIGN_BOUNDS.coord.max - 1;
    healed += this.repairOutliers(
      (a) =>
        Math.abs(a.x) >= boundary ||
        Math.abs(a.y) >= boundary ||
        Math.hypot(a.x - cx, a.y - cy) > limit,
    );
    if (healed === 0) {
      this.undoStack.pop();
      this.refreshUndoButtons();
      this.showMessage('Rien d’aberrant détecté — utilisez ✋ Éditer pour ajuster les points.');
      return;
    }
    this.drag = null;
    this.selection = null;
    this.lastPaintedSeg = null;
    this.track = null;
    this.fitView();
    this.mutated();
    this.showMessage(`🩹 ${healed} point(s) réparé(s) — Ctrl+Z pour revenir en arrière.`);
  }

  /**
   * Heals drafts authored before the pen threshold existed: anchors whose two
   * handles are both ~zero-length were click wobble, not intent — restore
   * their smooth auto handles (recomputed by the next refreshAutoHandles).
   */
  private healMicroHandles(): void {
    let healed = 0;
    for (const a of this.design.anchors) {
      if (a.auto) continue;
      const inLen = Math.hypot(a.inX, a.inY);
      const outLen = Math.hypot(a.outX, a.outY);
      if (inLen < MICRO_HANDLE && outLen < MICRO_HANDLE) {
        a.auto = true;
        healed++;
      }
    }
    if (healed > 0) {
      console.info(`éditeur : ${healed} point(s) anguleux réparé(s) (micro-poignées lissées)`);
    }
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (w === 0 || h === 0) return;
    const bw = Math.round(w * dpr);
    const bh = Math.round(h * dpr);
    if (this.canvas.width !== bw || this.canvas.height !== bh) {
      this.canvas.width = bw;
      this.canvas.height = bh;
    }
    this.requestRender();
  }

  private fitView(): void {
    const anchors = this.design.anchors;
    const { w, h } = this.cssSize();
    if (anchors.length === 0 || w === 0 || h === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    if (this.track) {
      minX = this.track.bounds.minX;
      minY = this.track.bounds.minY;
      maxX = this.track.bounds.maxX;
      maxY = this.track.bounds.maxY;
    } else {
      for (const a of anchors) {
        minX = Math.min(minX, a.x - a.width);
        minY = Math.min(minY, a.y - a.width);
        maxX = Math.max(maxX, a.x + a.width);
        maxY = Math.max(maxY, a.y + a.width);
      }
    }
    const pad = 60;
    this.viewScale = Math.min(
      (w - pad) / Math.max(maxX - minX, 1),
      (h - pad) / Math.max(maxY - minY, 1),
    );
    this.viewScale = Math.min(Math.max(this.viewScale, 0.04), 3);
    this.viewX = (minX + maxX) / 2;
    this.viewY = (minY + maxY) / 2;
    this.fitted = true;
  }

  private requestRender(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      this.render();
    });
  }

  private render(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { w, h } = this.cssSize();
    if (w === 0 || h === 0) return;
    // Moving the window to a display of another density changes dpr without
    // any CSS resize event — resync the backing store or everything stretches
    // until the next resize (audit M10). resize() no-ops when already synced.
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) {
      this.resize();
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#101318';
    ctx.fillRect(0, 0, w, h);

    // A drawing error must NEVER leave a silently half-blank canvas: report it
    // on the canvas itself so a playtest report carries the exact cause.
    try {
      this.renderGrid(ctx, w, h);
      // The ribbon (computed straight from the anchors) is ALWAYS drawn: it is
      // the road's safety net. The refined band paints over it in the normal
      // case; if the band ever goes wrong (bad build, divergent geometry), the
      // player still sees their road instead of floating points.
      this.renderOpenChain(ctx);
      if (this.closed && this.track) {
        this.renderBand(ctx, this.track);
        this.renderDirection(ctx, this.track);
        this.renderStartAndGates(ctx, this.track);
        this.renderIssues(ctx, this.track);
        this.renderDivergenceWarning(ctx, this.track);
      }
      this.renderSegmentHighlight(ctx);
      this.renderAnchors(ctx);
      this.renderDebugLine(ctx, h);
    } catch (err) {
      console.error('éditeur : erreur de rendu', err);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#ff4757';
      ctx.font = '13px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`Erreur d'affichage : ${String(err)}`, 12, 22);
      ctx.fillText('Appuyez sur Ctrl+Z pour annuler la dernière action.', 12, 40);
    }
  }

  private renderGrid(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const spacing = 250;
    const startX = Math.floor((this.viewX - w / 2 / this.viewScale) / spacing) * spacing;
    const endX = this.viewX + w / 2 / this.viewScale;
    const startY = Math.floor((this.viewY - h / 2 / this.viewScale) / spacing) * spacing;
    const endY = this.viewY + h / 2 / this.viewScale;
    ctx.fillStyle = 'rgba(255,255,255,0.07)';
    for (let x = startX; x <= endX; x += spacing) {
      for (let y = startY; y <= endY; y += spacing) {
        ctx.fillRect(this.sx(x) - 1, this.sy(y) - 1, 2, 2);
      }
    }
  }

  private renderBand(ctx: CanvasRenderingContext2D, track: Track): void {
    const samples = track.samples;
    const n = samples.length;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // One polyline per surface run keeps the stroke count low.
    let runStart = 0;
    for (let i = 1; i <= n; i++) {
      const prev = samples[(i - 1) % n] as TrackSample;
      const cur = samples[i % n] as TrackSample;
      const surfaceChanges = cur.surface !== prev.surface;
      const widthJump = Math.abs(cur.halfWidth - prev.halfWidth) > 2;
      if (i === n || surfaceChanges || widthJump || i - runStart >= 24) {
        const first = samples[runStart] as TrackSample;
        ctx.strokeStyle = SURFACE_COLORS[first.surface];
        ctx.lineWidth = Math.max(2, first.halfWidth * 2 * this.viewScale);
        ctx.beginPath();
        ctx.moveTo(this.sx(first.x), this.sy(first.y));
        for (let k = runStart + 1; k <= i; k++) {
          const smp = samples[k % n] as TrackSample;
          ctx.lineTo(this.sx(smp.x), this.sy(smp.y));
        }
        ctx.stroke();
        runStart = i;
      }
    }
    // Centerline.
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.sx((samples[0] as TrackSample).x), this.sy((samples[0] as TrackSample).y));
    for (let i = 1; i <= n; i++) {
      const smp = samples[i % n] as TrackSample;
      ctx.lineTo(this.sx(smp.x), this.sy(smp.y));
    }
    ctx.stroke();
  }

  private renderDirection(ctx: CanvasRenderingContext2D, track: Track): void {
    const every = 420;
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    for (let s = every / 2; s < track.length; s += every) {
      const idx = Math.round(s / track.step) % track.samples.length;
      const smp = track.samples[idx] as TrackSample;
      const size = Math.max(6, 9 * this.viewScale * 10) / 10;
      const cx = this.sx(smp.x);
      const cy = this.sy(smp.y);
      const tx = smp.tx;
      const ty = smp.ty;
      ctx.beginPath();
      ctx.moveTo(cx + tx * size, cy + ty * size);
      ctx.lineTo(cx - tx * size * 0.6 - ty * size * 0.6, cy - ty * size * 0.6 + tx * size * 0.6);
      ctx.lineTo(cx - tx * size * 0.6 + ty * size * 0.6, cy - ty * size * 0.6 - tx * size * 0.6);
      ctx.closePath();
      ctx.fill();
    }
  }

  private renderStartAndGates(ctx: CanvasRenderingContext2D, track: Track): void {
    // Start line (always visible).
    const s0 = track.samples[0] as TrackSample;
    const nx = -s0.ty;
    const ny = s0.tx;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(3, 6 * this.viewScale);
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(this.sx(s0.x - nx * s0.halfWidth), this.sy(s0.y - ny * s0.halfWidth));
    ctx.lineTo(this.sx(s0.x + nx * s0.halfWidth), this.sy(s0.y + ny * s0.halfWidth));
    ctx.stroke();
    ctx.setLineDash([]);

    if (this.mode !== 'start') return;

    // Checkpoints (authoring aid — invisible in game).
    ctx.strokeStyle = 'rgba(255, 176, 46, 0.7)';
    ctx.lineWidth = 1.5;
    for (const s of this.checkpoints) {
      const idx = Math.round(s / track.step) % track.samples.length;
      const smp = track.samples[idx] as TrackSample;
      const gx = -smp.ty;
      const gy = smp.tx;
      ctx.beginPath();
      ctx.moveTo(this.sx(smp.x - gx * smp.halfWidth), this.sy(smp.y - gy * smp.halfWidth));
      ctx.lineTo(this.sx(smp.x + gx * smp.halfWidth), this.sy(smp.y + gy * smp.halfWidth));
      ctx.stroke();
    }

    // Ghost grid: exactly where the 8 cars will stand (shared server logic).
    for (let slot = 0; slot < 8; slot++) {
      const pose = gridSlotPose(track, slot);
      const cx = this.sx(pose.x);
      const cy = this.sy(pose.y);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(pose.angle);
      const len = 26 * this.viewScale;
      const wid = 14 * this.viewScale;
      ctx.fillStyle = slot === 0 ? 'rgba(46, 213, 115, 0.5)' : 'rgba(255,255,255,0.22)';
      ctx.fillRect(-len / 2, -wid / 2, len, wid);
      ctx.restore();
    }
  }

  private renderIssues(ctx: CanvasRenderingContext2D, track: Track): void {
    if (this.issues.length === 0) return;
    const located = this.issues.filter((i) => i.s !== undefined);
    located.forEach((issue, index) => {
      const focused = index === this.issueCursor;
      for (const s of [issue.s, issue.s2]) {
        if (s === undefined) continue;
        const idx = Math.round(s / track.step) % track.samples.length;
        const smp = track.samples[idx] as TrackSample;
        const x = this.sx(smp.x);
        const y = this.sy(smp.y);
        const r = Math.max(18, (smp.halfWidth + 22) * this.viewScale);
        // Filled translucent disc + ring: unmissable at any zoom level.
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = focused ? 'rgba(255, 71, 87, 0.35)' : 'rgba(255, 71, 87, 0.18)';
        ctx.fill();
        ctx.strokeStyle = '#ff4757';
        ctx.lineWidth = focused ? 3 : 2;
        ctx.stroke();
        // Number badge so the chip's count maps to spots on the canvas.
        ctx.fillStyle = '#ff6b81';
        ctx.font = 'bold 13px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(index + 1), x, y - r - 6);
        ctx.textAlign = 'left';
      }
    });
  }

  private renderOpenChain(ctx: CanvasRenderingContext2D): void {
    const anchors = this.design.anchors;
    if (anchors.length === 0) {
      if (!this.closed) {
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = '14px system-ui, sans-serif';
        ctx.textAlign = 'center';
        const { w, h } = this.cssSize();
        ctx.fillText('Cliquez pour poser le premier point du tracé', w / 2, h / 2);
        ctx.textAlign = 'left';
      }
      return;
    }
    // Ribbon computed straight from the anchors — while drawing it IS the
    // preview; on a closed loop it underlays the refined band as a safety net.
    const polys = this.polylines();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (let i = 0; i < polys.length; i++) {
      const a = anchors[i] as DesignAnchor;
      ctx.strokeStyle = SURFACE_COLORS[a.surface];
      ctx.globalAlpha = this.closed ? 1 : 0.85;
      ctx.lineWidth = Math.max(2, a.width * this.viewScale);
      const pts = polys[i] as SegPolyPoint[];
      ctx.beginPath();
      ctx.moveTo(this.sx((pts[0] as SegPolyPoint).x), this.sy((pts[0] as SegPolyPoint).y));
      for (const p of pts) ctx.lineTo(this.sx(p.x), this.sy(p.y));
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    // Dashed hint from the last anchor back to the first (the future closure).
    if (!this.closed && anchors.length >= 2) {
      const last = anchors[anchors.length - 1] as DesignAnchor;
      const first = anchors[0] as DesignAnchor;
      ctx.strokeStyle = this.hoverClose ? 'rgba(46,213,115,0.9)' : 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([8, 6]);
      ctx.beginPath();
      ctx.moveTo(this.sx(last.x), this.sy(last.y));
      ctx.lineTo(this.sx(first.x), this.sy(first.y));
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  /**
   * Sanity net: if the refined band was built somewhere ELSE than the drawn
   * anchors (should be impossible — the band derives from them), scream on the
   * canvas and dump the design for a bug report instead of hiding the road.
   */
  private renderDivergenceWarning(ctx: CanvasRenderingContext2D, track: Track): void {
    const anchors = this.design.anchors;
    if (anchors.length === 0) return;
    let ax = 0;
    let ay = 0;
    for (const a of anchors) {
      ax += a.x;
      ay += a.y;
    }
    ax /= anchors.length;
    ay /= anchors.length;
    const tx = (track.bounds.minX + track.bounds.maxX) / 2;
    const ty = (track.bounds.minY + track.bounds.maxY) / 2;
    const gap = Math.hypot(tx - ax, ty - ay);
    if (gap < 2000) return;
    if (!this.divergenceLogged) {
      this.divergenceLogged = true;
      console.error(
        'éditeur : piste construite loin des points (écart ' + gap.toFixed(0) + ' px) — design :',
        JSON.stringify(roundDesign(this.design)),
      );
    }
    ctx.fillStyle = '#ff4757';
    ctx.font = '13px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(
      `⚠ Piste construite à ${Math.round(gap)} px des points — signalez ce message.`,
      12,
      22,
    );
  }

  /** Discreet dev status line — turns a screenshot into a diagnosis. */
  private renderDebugLine(ctx: CanvasRenderingContext2D, h: number): void {
    const parts = [
      `zoom ${this.viewScale.toFixed(2)}`,
      `vue (${Math.round(this.viewX)}, ${Math.round(this.viewY)})`,
      `points ${this.design.anchors.length}${this.closed ? ' (boucle fermée)' : ''}`,
      this.track ? `piste ${this.track.samples.length} éch.` : 'piste : —',
      this.issues.length > 0 ? `⚠ ${this.issues.length}` : '',
    ];
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(parts.filter(Boolean).join(' · '), 8, h - 8);
  }

  private renderSegmentHighlight(ctx: CanvasRenderingContext2D): void {
    if (this.hoverSeg === null || this.mode !== 'surface') return;
    const polys = this.polylines();
    const pts = polys[this.hoverSeg];
    if (!pts) return;
    const a = this.design.anchors[this.hoverSeg] as DesignAnchor;
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = Math.max(2, (a.width + 8) * this.viewScale);
    ctx.globalAlpha = 0.25;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(this.sx((pts[0] as SegPolyPoint).x), this.sy((pts[0] as SegPolyPoint).y));
    for (const p of pts) ctx.lineTo(this.sx(p.x), this.sy(p.y));
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private renderAnchors(ctx: CanvasRenderingContext2D): void {
    const anchors = this.design.anchors;
    anchors.forEach((a, i) => {
      const x = this.sx(a.x);
      const y = this.sy(a.y);
      const selected = i === this.selection;
      const hovered = i === this.hoverAnchor;

      // Width halo in width mode: the actual corridor extent at this anchor.
      if (this.mode === 'width') {
        ctx.strokeStyle = selected ? 'rgba(23,192,235,0.8)' : 'rgba(255,255,255,0.22)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, (a.width / 2) * this.viewScale, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Handles of the selected anchor in edit mode.
      if (this.mode === 'edit' && selected) {
        const hx1 = this.sx(a.x + a.inX);
        const hy1 = this.sy(a.y + a.inY);
        const hx2 = this.sx(a.x + a.outX);
        const hy2 = this.sy(a.y + a.outY);
        ctx.strokeStyle = 'rgba(23,192,235,0.7)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(hx1, hy1);
        ctx.lineTo(x, y);
        ctx.lineTo(hx2, hy2);
        ctx.stroke();
        ctx.fillStyle = '#17c0eb';
        ctx.fillRect(hx1 - 3, hy1 - 3, 6, 6);
        ctx.fillRect(hx2 - 3, hy2 - 3, 6, 6);
      }

      const closable = this.hoverClose && i === 0;
      const r = closable ? 9 : selected ? 7 : 5;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = closable
        ? '#2ed573'
        : selected
          ? '#17c0eb'
          : hovered
            ? '#ffffff'
            : 'rgba(255,255,255,0.85)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      if (i === 0 && !this.closed) {
        ctx.strokeStyle = 'rgba(46,213,115,0.9)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, r + 4, 0, Math.PI * 2);
        ctx.stroke();
      }
    });
  }
}

function isEditingContext(e: KeyboardEvent): boolean {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return false;
  return (
    t instanceof HTMLInputElement ||
    t instanceof HTMLTextAreaElement ||
    t instanceof HTMLSelectElement ||
    t.isContentEditable
  );
}
